/**
 * F1–F12 keyboard shortcuts (SOW Module 1). Each screen has one fixed map, shown on screen in the key bar, so a
 * cashier never has to reach for the mouse. Counter PCs run the browser in kiosk mode, where F5/F11/F12 are free.
 */
export const FKEYS = ["F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11", "F12"] as const;
export type FKey = (typeof FKEYS)[number];
export interface Shortcut<A extends string> { action: A; label: string }
export type KeyMap<A extends string> = Partial<Record<FKey, Shortcut<A>>>;

export type SellAction = "search" | "payCash" | "payUpi" | "paySplit" | "checkout" | "clearCart" | "reprintLast" | "history"
  | "shiftSummary" | "fastMode" | "customerDisplay" | "lock";

export const SELL_KEYS: KeyMap<SellAction> = {
  F1: { action: "search", label: "Scan / search" },
  F2: { action: "payCash", label: "Cash" },
  F3: { action: "payUpi", label: "UPI QR" },
  F4: { action: "paySplit", label: "Split" },
  F5: { action: "checkout", label: "Pay & print" },
  F6: { action: "clearCart", label: "Clear bill" },
  F7: { action: "reprintLast", label: "Reprint last" },
  F8: { action: "history", label: "History" },
  F9: { action: "shiftSummary", label: "Shift" },
  F10: { action: "fastMode", label: "Fast mode" },
  F11: { action: "customerDisplay", label: "Customer screen" },
  F12: { action: "lock", label: "Lock" },
};

export type FastAction = "preset1" | "preset2" | "preset3" | "preset4" | "toggleUpi" | "repeatLast" | "reprintLast" | "history"
  | "shiftSummary" | "fullMode" | "customerDisplay" | "lock";

export const FAST_KEYS: KeyMap<FastAction> = {
  F1: { action: "preset1", label: "Preset 1" },
  F2: { action: "preset2", label: "Preset 2" },
  F3: { action: "preset3", label: "Preset 3" },
  F4: { action: "preset4", label: "Preset 4" },
  F5: { action: "repeatLast", label: "Repeat last" },
  F6: { action: "toggleUpi", label: "Cash / UPI" },
  F7: { action: "reprintLast", label: "Reprint last" },
  F8: { action: "history", label: "History" },
  F9: { action: "shiftSummary", label: "Shift" },
  F10: { action: "fullMode", label: "Full POS" },
  F11: { action: "customerDisplay", label: "Customer screen" },
  F12: { action: "lock", label: "Lock" },
};

export function actionFor<A extends string>(map: KeyMap<A>, key: string): A | null {
  return (FKEYS as readonly string[]).includes(key) ? map[key as FKey]?.action ?? null : null;
}

/**
 * The action one key press runs. The browser's own use of a mapped key (F5 reload) is always suppressed, but an
 * auto-repeat from a held or stuck key never acts: in Fast mode every press issues and prints a ticket.
 */
export function onFnKey<A extends string>(map: KeyMap<A>, e: { key: string; repeat: boolean; preventDefault(): void }, blocked: boolean): A | null {
  const action = actionFor(map, e.key);
  if (!action) return null;
  e.preventDefault();
  return e.repeat || blocked ? null : action;
}
